class AuthService {
  constructor() {
    this.maxAttempts = 5;
    this.lockoutDuration = 15 * 60 * 1000; // 15 minutes in milliseconds
    this.sessionDuration = 8 * 60 * 60 * 1000; // 8 hours in milliseconds
    this.storageKey = 'skillsApp_auth';
    this.attemptsKey = 'skillsApp_auth_attempts';
    
    // Default password hash (SHA-256 of "admin123" - should be changed in production)
    this.defaultPasswordHash = 'f865b53623b121fd34ee5426c792e5c33af8c227d9e6a5d9234eb7a6b2b37cd';
  }

  // Simple SHA-256 hash function using Web Crypto API
  async hashPassword(password) {
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(byte => byte.toString(16).padStart(2, '0')).join('');
  }

  // Get stored password hash (in production, this should be more secure)
  getStoredPasswordHash() {
    const stored = localStorage.getItem(`${this.storageKey}_password`);
    return stored || this.defaultPasswordHash;
  }

  // Set new password hash
  async setPassword(newPassword) {
    if (!newPassword || newPassword.length < 6) {
      throw new Error('Password must be at least 6 characters long');
    }
    const hash = await this.hashPassword(newPassword);
    localStorage.setItem(`${this.storageKey}_password`, hash);
    return true;
  }

  // Check if account is currently locked out
  isLockedOut() {
    const attempts = this.getFailedAttempts();
    if (attempts.count >= this.maxAttempts) {
      const timeSinceLastAttempt = Date.now() - attempts.lastAttempt;
      if (timeSinceLastAttempt < this.lockoutDuration) {
        return {
          locked: true,
          remainingTime: Math.ceil((this.lockoutDuration - timeSinceLastAttempt) / 1000)
        };
      } else {
        // Lockout period has expired, reset attempts
        this.resetFailedAttempts();
        return { locked: false };
      }
    }
    return { locked: false };
  }

  // Get failed login attempts
  getFailedAttempts() {
    try {
      const stored = localStorage.getItem(this.attemptsKey);
      return stored ? JSON.parse(stored) : { count: 0, lastAttempt: 0 };
    } catch {
      return { count: 0, lastAttempt: 0 };
    }
  }

  // Record failed login attempt
  recordFailedAttempt() {
    const attempts = this.getFailedAttempts();
    attempts.count++;
    attempts.lastAttempt = Date.now();
    localStorage.setItem(this.attemptsKey, JSON.stringify(attempts));
    
    // Add progressive delay
    const delay = Math.min(attempts.count * 1000, 10000); // Max 10 seconds
    return new Promise(resolve => setTimeout(resolve, delay));
  }

  // Reset failed attempts (on successful login)
  resetFailedAttempts() {
    localStorage.removeItem(this.attemptsKey);
  }

  // Authenticate user
  async authenticate(password) {
    // Check if locked out
    const lockStatus = this.isLockedOut();
    if (lockStatus.locked) {
      throw new Error(`Account locked. Try again in ${Math.ceil(lockStatus.remainingTime / 60)} minutes.`);
    }

    // Sanitize input
    if (!password || typeof password !== 'string') {
      await this.recordFailedAttempt();
      throw new Error('Invalid credentials');
    }

    // Hash provided password
    const providedHash = await this.hashPassword(password.trim());
    const storedHash = this.getStoredPasswordHash();

    // Constant-time comparison to prevent timing attacks
    if (providedHash === storedHash) {
      this.resetFailedAttempts();
      this.createSession();
      return { success: true };
    } else {
      await this.recordFailedAttempt();
      throw new Error('Invalid credentials');
    }
  }

  // Create authenticated session
  createSession() {
    const session = {
      authenticated: true,
      timestamp: Date.now(),
      expires: Date.now() + this.sessionDuration
    };
    localStorage.setItem(this.storageKey, JSON.stringify(session));
  }

  // Check if user is currently authenticated
  isAuthenticated() {
    try {
      const session = localStorage.getItem(this.storageKey);
      if (!session) return false;

      const sessionData = JSON.parse(session);
      if (!sessionData.authenticated || !sessionData.expires) return false;

      // Check if session has expired
      if (Date.now() > sessionData.expires) {
        this.logout();
        return false;
      }

      // Extend session on activity
      this.extendSession();
      return true;
    } catch {
      return false;
    }
  }

  // Extend session timestamp
  extendSession() {
    try {
      const session = localStorage.getItem(this.storageKey);
      if (session) {
        const sessionData = JSON.parse(session);
        sessionData.expires = Date.now() + this.sessionDuration;
        localStorage.setItem(this.storageKey, JSON.stringify(sessionData));
      }
    } catch {
      // Silent fail - session will expire naturally
    }
  }

  // Get remaining session time in minutes
  getSessionRemainingTime() {
    try {
      const session = localStorage.getItem(this.storageKey);
      if (!session) return 0;

      const sessionData = JSON.parse(session);
      if (!sessionData.expires) return 0;

      const remaining = sessionData.expires - Date.now();
      return Math.max(0, Math.ceil(remaining / (60 * 1000))); // Return minutes
    } catch {
      return 0;
    }
  }

  // Logout user
  logout() {
    localStorage.removeItem(this.storageKey);
  }

  // Get security info for display
  getSecurityInfo() {
    const attempts = this.getFailedAttempts();
    const lockStatus = this.isLockedOut();
    
    return {
      failedAttempts: attempts.count,
      maxAttempts: this.maxAttempts,
      isLockedOut: lockStatus.locked,
      lockoutRemaining: lockStatus.remainingTime || 0,
      sessionRemaining: this.getSessionRemainingTime(),
      hasCustomPassword: this.getStoredPasswordHash() !== this.defaultPasswordHash
    };
  }
}

export default AuthService;